import React from "react";
import { Link } from "react-router-dom";
import { Table, Space, Button, Tag, Popconfirm, message } from "antd";
import {
  EditOutlined,
  DisconnectOutlined,
  ApartmentOutlined,
} from "@ant-design/icons";
import { ListComponent, BaseListComponentProps } from "../BaseComponent";
import IIntegration from "../../interfaces/IIntegration";
import { SearchRequest } from "../../services/api/interfaces/ApiRequest";
import {
  DisconnectRequest,
  SetMainRequest,
} from "../../services/integration/interfaces/ApiRequest";
import { Pagination } from "components/Pagination";

type IntegrationListProps = BaseListComponentProps & {
  integrations: any;
  pagination: any;
  loading: boolean;

  history: any;
  searchOptions: any;

  loadIntegrations: (args: SearchRequest) => void;
  disconnectIntegration: (args: DisconnectRequest) => Promise<any>;
  setMainIntegration: (args: SetMainRequest) => Promise<any>;
};

export default class IntegrationList extends ListComponent<IntegrationListProps> {
  static defaultProps = {
    loading: false,
  };

  state = {
    removed: [] as string[],
  };

  tableColumns = [
    {
      title: "UUID",
      dataIndex: "uuid",
      key: "uuid",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Shelter",
      dataIndex: ["shelter", "name"],
      key: "shelter",
      responsive: ["md"] as any,
    },
    {
      title: "Location",
      dataIndex: ["branch", "name"],
      key: "branch",
      responsive: ["md"] as any,
    },
    {
      title: "Partner",
      dataIndex: "partner_key",
      key: "partner_key",
      responsive: ["md"] as any,
      sorter: (a: any, b: any) => a.partner_key?.length - b.partner_key?.length,
    },
    // {
    //   title: "Last Sync",
    //   dataIndex: "last_sync",
    //   key: "last_sync",
    //   responsive: ["md"] as any,
    //   defaultSortOrder: "ascend",
    //   sorter: (a: any, b: any) => a.last_sync - b.last_sync,
    //   render: (text: string, record: any) => {
    //     if (!record.last_sync) return <Tag color="orange">Never</Tag>;
    //     if (!record.integration_syncs) return null;

    //     // Mount syncs popover
    //     const [sync] = record.integration_syncs;
    //     let syncId = "";
    //     let popoverContent: any = null;

    //     if (sync) {
    //       syncId = `#${sync.id}`;

    //       const timeline = { ...IntegrationConstants.SYNC_TIMELINE };
    //       Object.keys(timeline).forEach((key) => {
    //         const item = sync[key];
    //         if (item) {
    //           timeline[key] = {
    //             ...timeline[key],
    //             color: "blue",
    //             value: item,
    //           };
    //         }

    //         if (!item && !timeline[key].custom) {
    //           delete timeline[key];
    //         }
    //       });

    //       // Has finished?
    //       if (sync.finish) {
    //         timeline["status"] = {
    //           ...timeline["status"],
    //           color: "green",
    //           label: "Finished",
    //           value: sync.process_time,
    //         };
    //       }

    //       // Has been canceled
    //       if (sync.canceled) {
    //         timeline["status"] = {
    //           ...timeline["status"],
    //           color: "red",
    //           label: "Canceled",
    //           value: sync.process_time,
    //         };
    //       }

    //       popoverContent = <DynamicTimeline items={timeline} />;
    //     }

    //     const date = moment(record.last_sync).calendar(null, {
    //       sameDay: "[Today]",
    //       lastDay: "[Yesterday]",
    //       sameElse: "YYYY-MM-DD HH:mm",
    //     });

    //     return (
    //       <Popover
    //         content={popoverContent}
    //         title={`Sync ${syncId}`}
    //         trigger="hover"
    //       >
    //         <Tag color="blue">{date}</Tag>
    //       </Popover>
    //     );
    //   },
    // },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      responsive: ["md"] as any,
      render: (text: string, record: IIntegration) => {
        const finished = record.integration_syncs?.length
          ? record.integration_syncs[0].finish
          : false;
        const canceled = record.integration_syncs?.length
          ? record.integration_syncs[0].canceled
          : false;

        // Is running?
        const running = !finished && !canceled;

        let status = "Unknown";
        let color = "purple";
        if (running) {
          status = "Running";
          color = "processing";
        } else if (finished) {
          status = "Finished";
          color = "green";
        } else if (canceled) {
          status = "Canceled";
          color = "volcano";
        }

        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: "Main",
      dataIndex: "main",
      key: "main",
      responsive: ["md"] as any,
      sorter: (a: any, b: any) => a.main - b.main,
      render: (text: string, record: IIntegration) => {
        return (
          <Tag color={record.main ? "green" : "geekblue"}>
            {record.main ? "Yes" : "No"}
          </Tag>
        );
      },
    },
    {
      title: "Actions",
      key: "action",
      align: "right",
      render: (text: string, record: any) => (
        <Space size="middle">
          <Link to={`/integration/${record.uuid}`}>
            <Button type="dashed" shape="round" icon={<EditOutlined />} />
          </Link>

          <Popconfirm
            title="Do you wish to disconnect this integration? Be careful with this action."
            okButtonProps={{ loading: this.props.loading }}
            okText="Yes"
            cancelText="No"
            onConfirm={() => this.handleDisconnect(record.uuid)}
          >
            <Button type="dashed" shape="round" icon={<DisconnectOutlined />} />
          </Popconfirm>

          {!record.main && (
            <Popconfirm
              title="Do you really want to set this integration as main?"
              okButtonProps={{ loading: this.props.loading }}
              okText="Yes"
              cancelText="No"
              onConfirm={() => this.handleSetMain(record.uuid)}
            >
              <Button
                type="dashed"
                shape="round"
                icon={<ApartmentOutlined />}
              />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ] as any;

  protected load() {
    this.searchOptions = {
      search: {
        ...this.props.searchOptions.search,
      },
      options: {
        ...this.props.searchOptions.options,
      },
    };

    this.props.loadIntegrations(this.searchOptions);
  }

  private handleDisconnect = async (uuid: string) => {
    const { disconnectIntegration } = this.props;
    const { removed } = this.state;

    try {
      await disconnectIntegration({
        integration_uuid: uuid,
      });

      message.success("Integration disconnected successfully!");
      removed.push(uuid);

      this.setState({
        removed,
      });
    } catch (error) {
      message.error(`Could not disconnect integration. Reason: ${error}`);
    }
  };

  private handleSetMain = async (uuid: string) => {
    const { setMainIntegration } = this.props;

    try {
      await setMainIntegration({
        integration_uuid: uuid,
      });

      this.load();

      message.success("Integration is now the main!");
    } catch (error) {
      message.error(`Could not set main integration. Reason: ${error}`);
    }
  };

  render() {
    const { loading, pagination, integrations, loadIntegrations } = this.props;
    const { removed } = this.state;

    const data = integrations?.filter((v) => !removed.includes(v.uuid));
    return (
      <>
        <Table
          loading={loading}
          rowKey="uuid"
          columns={this.tableColumns}
          pagination={false}
          dataSource={data}
          onChange={(pagination: any, filters: any, sorter: any) =>
            this.handleTableChange(pagination, sorter, loadIntegrations)
          }
        />
        <Pagination
          loading={loading}
          current={pagination?.page}
          pageSize={pagination?.limit}
          length={data?.length}
          onChange={(pagination) =>
            this.handleTableChange(pagination, undefined, loadIntegrations)
          }
        />
      </>
    );
  }
}
